import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faEdit, faImage, faTimes } from '@fortawesome/free-solid-svg-icons';
import CommonTable from '../../table/ComonTable';
import PageHeadingSection from '../../pageheading/PageHeadingSection';
import { amenityCommonApi } from '../amenityCommonApi';

const AmenityTypeList = ({ siteId, authUser }) => {
    const [types, setTypes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [editId, setEditId] = useState(null);

    const [formData, setFormData] = useState({
        amenityTypeName: '',
        image: '',
        isActiveId: 1
    });

    useEffect(() => {
        fetchTypes();
    }, []);

    const fetchTypes = async () => {
        setLoading(true);
        try {
            const res = await amenityCommonApi.getAllAmenityTypes();
            if ((res.data?.isSuccess || res.data?.IsSuccess) && Array.isArray(res.data.data)) {
                // sort descending by ID
                const sorted = res.data.data.sort((a, b) => b.amenityTypeId - a.amenityTypeId);
                setTypes(sorted);
            } else {
                setTypes([]);
                if (res.data && !(res.data.isSuccess || res.data.IsSuccess)) {
                    toast.error(res.data.message || res.data.Message || 'Failed to load amenity types.');
                }
            }
        } catch (error) {
            toast.error('An error occurred while fetching amenity types.');
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData({
            ...formData,
            [name]: type === 'checkbox' ? (checked ? 1 : 0) : value
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.amenityTypeName.trim()) {
            toast.error("Type Name is required.");
            return;
        }

        const payload = {
            amenityTypeId: isEditMode ? editId : 0,
            amenityTypeName: formData.amenityTypeName,
            siteId: siteId,
            image: formData.image,
            isActiveId: formData.isActiveId,
            [isEditMode ? 'modifiedBy' : 'createdBy']: authUser?.email || String(siteId)
        };

        try {
            let res;
            if (isEditMode) {
                res = await amenityCommonApi.updateAmenityType(editId, payload);
            } else {
                res = await amenityCommonApi.createAmenityType(payload);
            }

            if (res.data?.isSuccess || res.data?.IsSuccess) {
                toast.success(res.data.message || res.data.Message || `Amenity Type ${isEditMode ? 'updated' : 'created'} successfully!`);
                setIsFormOpen(false);
                resetForm();
                fetchTypes();
            } else {
                toast.error(res.data?.message || res.data?.Message || `Failed to ${isEditMode ? 'update' : 'create'} amenity type.`);
            }
        } catch (error) {
            toast.error(`An error occurred while ${isEditMode ? 'updating' : 'creating'} the amenity type.`);
        }
    };

    const handleEdit = (row) => {
        setIsEditMode(true);
        setEditId(row.amenityTypeId);
        setFormData({
            amenityTypeName: row.amenityTypeName || '',
            image: row.image || '',
            isActiveId: row.isActiveId !== undefined ? row.isActiveId : 1
        });
        setIsFormOpen(true);
    };

    const resetForm = () => {
        setIsEditMode(false);
        setEditId(null);
        setFormData({
            amenityTypeName: '',
            image: '',
            isActiveId: 1
        });
    };

    const toggleForm = () => {
        if (isFormOpen) resetForm();
        setIsFormOpen(!isFormOpen);
    };

    const tableHeaders = [
        { label: "S. No", key: "sno", align: "center" },
        { label: "TYPE NAME", key: "amenityTypeName", align: "left" },
        { label: "IMAGE", key: "image", align: "center" },
        { label: "STATUS", key: "status", align: "center" },
        { label: "ACTIONS", key: "actionButtons", align: "center" },
    ];

    const renderCell = (key, row, header) => {
        switch (key) {
            case "sno":
                return <span className="font-semibold text-xs">{types.indexOf(row) + 1}</span>;
            case "amenityTypeName":
                return <span className="font-bold text-gray-800 tracking-tight">{row.amenityTypeName}</span>;
            case "image":
                return (
                    <div className="flex justify-center items-center">
                        {row.image ? (
                            <img src={row.image} alt="Type" className="h-8 w-8 rounded object-cover shadow-sm" />
                        ) : (
                            <span className="text-gray-400"><FontAwesomeIcon icon={faImage} /></span>
                        )}
                    </div>
                );
            case "status":
                const isActive = row.isActiveId === 1;
                const statusColor = isActive ? "bg-[#DCFCE7] text-[#166534]" : "bg-[#FEF9C3] text-[#D97706]";
                return (
                    <span className={`px-3 py-1 rounded-full text-[10px] font-black shadow-sm uppercase ${statusColor}`}>
                        {isActive ? 'Active' : 'Inactive'}
                    </span>
                );
            case "actionButtons":
                return (
                    <button 
                        onClick={() => handleEdit(row)}
                        className="text-indigo-600 hover:text-indigo-800 transition-colors"
                        title="Edit Type"
                    >
                        <FontAwesomeIcon icon={faEdit} />
                    </button>
                );
            default:
                return row[key] || "-";
        }
    };

    return (
        <div className="flex flex-col gap-6">
            <div className="flex justify-between items-center">
                <div>
                    <h3 className="text-xl font-bold text-gray-800">Amenity Types</h3>
                    <p className="text-sm text-gray-500">Manage categories like Gym, Pool, Clubhouse, etc.</p>
                </div>
                <button
                    onClick={toggleForm}
                    className={`${isFormOpen ? 'bg-red-50 hover:bg-red-100 text-red-600 border border-red-200' : 'bg-indigo-600 hover:bg-indigo-700 text-white'} px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2`}
                >
                    <FontAwesomeIcon icon={isFormOpen ? faTimes : faPlus} />
                    {isFormOpen ? 'Cancel' : 'Add New Type'}
                </button>
            </div>

            {isFormOpen && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 animate-fade-in-down">
                    <h4 className="text-lg font-bold mb-4 text-gray-700">{isEditMode ? 'Edit Amenity Type' : 'Create Amenity Type'}</h4>
                    <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Type Name *</label>
                            <input
                                type="text"
                                name="amenityTypeName"
                                value={formData.amenityTypeName}
                                onChange={handleInputChange}
                                className="w-full border border-gray-300 rounded-lg p-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                required
                                placeholder="e.g., Swimming Pool"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Image URL</label>
                            <div className="flex gap-2">
                                <input
                                    type="text"
                                    name="image"
                                    value={formData.image}
                                    onChange={handleInputChange}
                                    className="flex-1 border border-gray-300 rounded-lg p-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                    placeholder="https://..."
                                />
                                {formData.image && (
                                    <div className="w-10 h-10 rounded border overflow-hidden shrink-0">
                                        <img src={formData.image} alt="preview" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none' }} />
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="flex items-center space-x-2 md:col-span-2">
                            <input
                                type="checkbox"
                                id="isActiveId"
                                name="isActiveId"
                                checked={formData.isActiveId === 1}
                                onChange={handleInputChange}
                                className="h-4 w-4 text-indigo-600 rounded border-gray-300 focus:ring-indigo-500"
                            />
                            <label htmlFor="isActiveId" className="text-sm font-medium text-gray-700 cursor-pointer">Active</label>
                        </div>
                        <div className="md:col-span-2 mt-2 flex justify-end">
                            <button
                                type="submit"
                                className="bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-6 rounded-lg transition-colors shadow-sm"
                            >
                                {isEditMode ? 'Update Type' : 'Save Type'}
                            </button>
                        </div>
                    </form>
                </div>
            )}

            <div className="border border-gray-200 rounded-lg overflow-hidden shadow-sm">
                <CommonTable
                    loading={loading}
                    tableHeading="All Amenity Types"
                    headers={tableHeaders}
                    data={types}
                    renderCell={renderCell}
                    pagination={null} // showing all for simplicity, or we can add pagination later
                />
            </div>
        </div>
    );
};

export default AmenityTypeList;
